<template>
  <div class="step-description-container">
    <!-- Заголовок -->
    <div class="step-header">
      <div class="step-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M7 3.5h7l4 4V19a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5.5a2 2 0 0 1 2-2Z"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linejoin="round"
          />

          <path
            d="M14 3.5V8h4M8.5 12h7M8.5 15.5h5"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>

      <div class="step-heading">
        <span class="step-label">
          О вакансии
        </span>

        <h3 class="step-heading-title">
          Описание вакансии
        </h3>
      </div>
    </div>

    <!-- Описание -->
    <p class="step-description">
      Расскажите, чем будет заниматься сотрудник, какие задачи его ждут
      и что вы предлагаете будущему члену команды.
    </p>

    <!-- Подсказки -->
    <div class="description-prompts">
      <div class="prompt">
        <span class="prompt-icon">01</span>

        <div>
          <strong>Задачи</strong>
          <span>Чем предстоит заниматься</span>
        </div>
      </div>

      <div class="prompt">
        <span class="prompt-icon">02</span>

        <div>
          <strong>Ожидания</strong>
          <span>Кого вы ищете</span>
        </div>
      </div>

      <div class="prompt">
        <span class="prompt-icon">03</span>

        <div>
          <strong>Условия</strong>
          <span>Что предлагает компания</span>
        </div>
      </div>
    </div>

    <!-- Поле -->
    <div class="field-container">
      <div class="field-heading">
        <label
          class="field-label"
          for="vacancy-description"
        >
          Подробное описание
        </label>

        <div class="field-meta">
          <Transition name="status">
            <span
              v-if="isValid"
              class="validation-status"
            >
              <svg
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  cx="10"
                  cy="10"
                  r="8"
                  fill="currentColor"
                />

                <path
                  d="m6.4 10.1 2.2 2.2 5-5"
                  stroke="white"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>

              Заполнено
            </span>
          </Transition>

          <span
            class="characters-counter"
            :class="{
              warning:
                descriptionLength >=
                MAX_DESCRIPTION_LENGTH - 200
            }"
          >
            {{ descriptionLength }}/{{ MAX_DESCRIPTION_LENGTH }}
          </span>
        </div>
      </div>

      <div
        class="textarea-shell"
        :class="{
          valid: isValid,
          error: showError,
          focused: isFocused
        }"
      >
        <textarea
          id="vacancy-description"
          ref="textareaRef"
          v-model="description"
          class="description-textarea"
          placeholder="Расскажите о задачах, требованиях, команде и условиях работы..."
          :maxlength="MAX_DESCRIPTION_LENGTH"
          spellcheck="true"
          @input="handleInput"
          @focus="isFocused = true"
          @blur="handleBlur"
        ></textarea>

        <div class="textarea-footer">
          <span class="minimum-hint">
            Минимум {{ MIN_DESCRIPTION_LENGTH }} символов
          </span>

          <span
            class="length-indicator"
            :class="{ complete: isValid }"
          >
            <span
              class="length-indicator-fill"
              :style="{ width: validationProgress }"
            ></span>
          </span>
        </div>
      </div>

      <!-- Сообщение -->
      <Transition name="message" mode="out-in">
        <div
          v-if="showError"
          key="error"
          class="field-message error-message"
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="10"
              cy="10"
              r="8"
              stroke="currentColor"
              stroke-width="1.6"
            />

            <path
              d="M10 6.3v4.4M10 13.6v.1"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>

          Добавьте ещё немного информации о вакансии
        </div>

        <div
          v-else
          key="hint"
          class="field-message hint-message"
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="10"
              cy="10"
              r="8"
              stroke="currentColor"
              stroke-width="1.5"
            />

            <path
              d="M10 9v5M10 6.2v.2"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>

          Разделяйте смысловые блоки пустой строкой — так описание легче читать
        </div>
      </Transition>
    </div>

    <!-- Рекомендация -->
    <div class="recommendation">
      <div class="recommendation-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M9 18h6M10 21h4"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />

          <path
            d="M8.5 15.5c-1.5-1.1-2.5-2.9-2.5-5a6 6 0 1 1 12 0c0 2.1-1 3.9-2.5 5"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />
        </svg>
      </div>

      <p>
        Хорошее описание отвечает на три вопроса кандидата:
        что я буду делать, с кем я буду работать и что получу взамен.
      </p>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  watch
} from "vue";

const emit = defineEmits([
  "complete-step",
  "valid",
  "notValid"
]);

/*
  Старая проверка была > 10,
  поэтому минимально валидное значение — 11 символов.
*/
const MIN_DESCRIPTION_LENGTH = 11;
const MAX_DESCRIPTION_LENGTH = 3000;

const description = ref(
  localStorage.getItem(
    "vacancy_description"
  ) || ""
);

const textareaRef = ref(null);
const isTouched = ref(false);
const isFocused = ref(false);

const normalizedDescription = computed(() =>
  description.value.trim()
);

const descriptionLength = computed(() =>
  description.value.length
);

const isValid = computed(() =>
  normalizedDescription.value.length >=
  MIN_DESCRIPTION_LENGTH
);

const showError = computed(() =>
  isTouched.value && !isValid.value
);

const validationProgress = computed(() => {
  const progress =
    (
      normalizedDescription.value.length /
      MIN_DESCRIPTION_LENGTH
    ) * 100;

  return `${Math.min(progress, 100)}%`;
});

function handleInput() {
  isTouched.value = true;
}

function handleBlur() {
  isFocused.value = false;

  if (description.value.length > 0) {
    isTouched.value = true;
  }
}

function saveDescriptionAndContinue() {
  isTouched.value = true;

  if (!isValid.value) {
    emit("notValid");

    console.warn(
      "⚠️ Description слишком короткое"
    );

    return;
  }

  description.value =
    normalizedDescription.value;

  localStorage.setItem(
    "vacancy_description",
    description.value
  );

  emit("complete-step");
}

watch(
  description,
  newValue => {
    localStorage.setItem(
      "vacancy_description",
      newValue
    );

    if (
      newValue.trim().length >=
      MIN_DESCRIPTION_LENGTH
    ) {
      emit("valid");
    } else {
      emit("notValid");
    }
  },
  {
    immediate: true
  }
);

onMounted(() => {
  document.addEventListener(
    "save-description",
    saveDescriptionAndContinue
  );
});

onUnmounted(() => {
  document.removeEventListener(
    "save-description",
    saveDescriptionAndContinue
  );
});
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.step-description-container {
  width: 100%;
  max-width: 720px;

  display: flex;
  flex-direction: column;

  margin-left: 40px;
  padding: 26px;

  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(48, 132, 255, 0.075),
      transparent 250px
    ),
    #ffffff;

  border: 1px solid rgba(30, 50, 80, 0.07);
  border-radius: 24px;

  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.02),
    0 12px 34px rgba(15, 23, 42, 0.04);

  animation: card-appearance 0.4s ease both;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 13px;

  margin-bottom: 17px;
}

.step-icon {
  width: 43px;
  height: 43px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #ffffff;

  background:
    linear-gradient(
      145deg,
      #36a1ff 0%,
      #4078f3 55%,
      #625bea 100%
    );

  border-radius: 14px;

  box-shadow:
    0 8px 18px rgba(48, 126, 246, 0.23),
    inset 0 1px 0 rgba(255, 255, 255, 0.24);
}

.step-icon svg {
  width: 23px;
  height: 23px;
}

.step-heading {
  min-width: 0;

  display: flex;
  flex-direction: column;
  gap: 2px;
}

.step-label {
  color: #8b94a4;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.step-heading-title {
  margin: 0;

  color: #20242c;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 18px;
  line-height: 1.25;
}

.step-description {
  max-width: 650px;
  margin: 0 0 18px;

  color: #697282;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;
  line-height: 1.65;
}

/* Подсказки */

.description-prompts {
  width: 100%;
  max-width: 650px;

  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 9px;

  margin-bottom: 22px;
}

.prompt {
  display: flex;
  align-items: center;
  gap: 9px;

  min-width: 0;
  padding: 10px;

  background: #f8f9fb;

  border: 1px solid #e8ebef;
  border-radius: 12px;
}

.prompt-icon {
  width: 28px;
  height: 28px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #4388f5;
  background: rgba(67, 136, 245, 0.09);

  border-radius: 9px;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 8px;
}

.prompt > div {
  min-width: 0;

  display: flex;
  flex-direction: column;
  gap: 2px;
}

.prompt strong {
  color: #414956;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 9px;
}

.prompt div span {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  color: #929aa7;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 8px;
}

/* Поле */

.field-container {
  width: 100%;
  max-width: 650px;
}

.field-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  margin-bottom: 8px;
}

.field-label {
  color: #424957;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 11px;
}

.field-meta {
  display: flex;
  align-items: center;
  gap: 10px;
}

.validation-status {
  display: flex;
  align-items: center;
  gap: 5px;

  color: #28a46b;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
  font-weight: 600;
}

.validation-status svg {
  width: 14px;
  height: 14px;
}

.characters-counter {
  color: #9aa1ad;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;

  transition: color 0.2s ease;
}

.characters-counter.warning {
  color: #d99035;
}

/* Textarea */

.textarea-shell {
  width: 100%;

  overflow: hidden;

  background: #f6f7f9;

  border: 1px solid #e1e4e9;
  border-radius: 15px;

  box-shadow:
    inset 0 1px 2px rgba(15, 23, 42, 0.025);

  transition:
    background-color 0.22s ease,
    border-color 0.22s ease,
    box-shadow 0.22s ease,
    transform 0.22s ease;
}

.textarea-shell:hover {
  background: #fafbfc;
  border-color: #d4d9e1;
}

.textarea-shell.focused {
  background: #ffffff;
  border-color: #4388f5;

  box-shadow:
    0 0 0 4px rgba(67, 136, 245, 0.11),
    0 8px 24px rgba(31, 96, 205, 0.08);

  transform: translateY(-1px);
}

.textarea-shell.valid:not(.focused) {
  border-color: rgba(39, 162, 104, 0.38);
}

.textarea-shell.error {
  background: rgba(255, 246, 246, 0.9);
  border-color: #e15b5b;

  box-shadow:
    0 0 0 4px rgba(225, 91, 91, 0.08);
}

.description-textarea {
  width: 100%;
  min-height: 210px;
  max-height: 380px;

  display: block;

  padding: 16px 17px;

  resize: vertical;

  color: #252b35;
  caret-color: #4388f5;

  background: transparent;
  border: none;
  outline: none;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;
  line-height: 1.65;
}

.description-textarea::placeholder {
  color: #a5acb7;
  opacity: 1;
}

.textarea-footer {
  min-height: 35px;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;

  padding: 0 14px;

  border-top: 1px solid rgba(35, 50, 70, 0.055);
}

.minimum-hint {
  color: #959daa;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 8px;
}

.length-indicator {
  width: 72px;
  height: 4px;

  overflow: hidden;

  background: #e1e5ea;
  border-radius: 999px;
}

.length-indicator-fill {
  display: block;

  height: 100%;

  background:
    linear-gradient(
      90deg,
      #4a9bf7,
      #3d76e6
    );

  border-radius: inherit;

  transition:
    width 0.25s ease,
    background 0.25s ease;
}

.length-indicator.complete
  .length-indicator-fill {
  background:
    linear-gradient(
      90deg,
      #39b875,
      #289b62
    );
}

/* Messages */

.field-message {
  min-height: 20px;

  display: flex;
  align-items: flex-start;
  gap: 7px;

  margin-top: 8px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  line-height: 1.45;
}

.field-message svg {
  width: 14px;
  height: 14px;

  flex-shrink: 0;
  margin-top: 1px;
}

.hint-message {
  color: #8a93a2;
}

.error-message {
  color: #d84e4e;
  font-weight: 600;
}

/* Recommendation */

.recommendation {
  max-width: 650px;

  display: flex;
  align-items: flex-start;
  gap: 10px;

  margin-top: 20px;
  padding: 12px 13px;

  color: #667084;
  background: rgba(53, 132, 247, 0.055);

  border: 1px solid rgba(53, 132, 247, 0.1);
  border-radius: 13px;
}

.recommendation-icon {
  width: 18px;
  height: 18px;

  flex-shrink: 0;

  color: #4388f5;
}

.recommendation-icon svg {
  width: 18px;
  height: 18px;
}

.recommendation p {
  margin: 0;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  line-height: 1.55;
}

/* Animations */

@keyframes card-appearance {
  from {
    opacity: 0;
    transform: translateY(7px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.status-enter-active,
.status-leave-active,
.message-enter-active,
.message-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.status-enter-from,
.status-leave-to {
  opacity: 0;
  transform: scale(0.7);
}

.message-enter-from {
  opacity: 0;
  transform: translateY(-3px);
}

.message-leave-to {
  opacity: 0;
  transform: translateY(3px);
}

/* Dark theme */

html[data-theme="dark"] .step-description-container {
  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(54, 132, 255, 0.09),
      transparent 250px
    ),
    #1c1c1f;

  border-color: rgba(255, 255, 255, 0.07);

  box-shadow:
    0 14px 38px rgba(0, 0, 0, 0.14);
}

html[data-theme="dark"] .step-heading-title {
  color: #f1f2f4;
}

html[data-theme="dark"] .step-label {
  color: #777f8d;
}

html[data-theme="dark"] .step-description {
  color: #a5a9b2;
}

html[data-theme="dark"] .prompt {
  background: #27272b;
  border-color: #37383e;
}

html[data-theme="dark"] .prompt strong {
  color: #dadce1;
}

html[data-theme="dark"] .prompt div span {
  color: #777e89;
}

html[data-theme="dark"] .prompt-icon {
  color: #6ba0ef;
  background: rgba(67, 136, 245, 0.1);
}

html[data-theme="dark"] .field-label {
  color: #d8dae0;
}

html[data-theme="dark"] .textarea-shell {
  background: #28282c;
  border-color: #3b3b41;
}

html[data-theme="dark"] .textarea-shell:hover {
  background: #2b2b30;
  border-color: #484850;
}

html[data-theme="dark"] .textarea-shell.focused {
  background: #2d2d32;
  border-color: #548ff1;

  box-shadow:
    0 0 0 4px rgba(78, 143, 245, 0.13),
    0 9px 24px rgba(0, 0, 0, 0.14);
}

html[data-theme="dark"] .textarea-shell.error {
  background: rgba(91, 37, 40, 0.2);
  border-color: #d85b60;
}

html[data-theme="dark"] .description-textarea {
  color: #f1f2f4;
  caret-color: #669cf5;
}

html[data-theme="dark"]
  .description-textarea::placeholder {
  color: #6f747e;
}

html[data-theme="dark"] .textarea-footer {
  border-top-color: rgba(255, 255, 255, 0.055);
}

html[data-theme="dark"] .minimum-hint {
  color: #747b86;
}

html[data-theme="dark"] .length-indicator {
  background: #41434a;
}

html[data-theme="dark"] .hint-message {
  color: #777d88;
}

html[data-theme="dark"] .recommendation {
  color: #9ba2ae;
  background: rgba(63, 137, 241, 0.07);
  border-color: rgba(74, 145, 246, 0.12);
}

/* Mobile */

@media (max-width: 430px) {
  .step-description-container {
    width: 100%;
    max-width: none;

    margin-left: 0;
    padding: 20px 16px;

    border-radius: 18px;
  }

  .step-icon {
    width: 39px;
    height: 39px;

    border-radius: 12px;
  }

  .step-heading-title {
    font-size: 16px;
  }

  .step-description {
    font-size: 12px;
    line-height: 1.55;
  }

  .description-prompts {
    grid-template-columns: 1fr;
    gap: 7px;
  }

  .prompt {
    padding: 9px 10px;
  }

  .field-container {
    max-width: none;
  }

  .description-textarea {
    min-height: 220px;
    max-height: 320px;

    padding: 14px;

    font-size: 14px;
    line-height: 1.55;

    resize: none;
  }

  .recommendation {
    max-width: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .step-description-container {
    animation: none;
  }

  .textarea-shell,
  .length-indicator-fill,
  .status-enter-active,
  .status-leave-active,
  .message-enter-active,
  .message-leave-active {
    transition: none;
  }
}
</style>